import React, { useEffect, useMemo } from 'react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';

/**
 * ParticleBackground - Animated particle system for ambient background effects
 *
 * Usage:
 * <ParticleBackground />
 *
 * This component creates a subtle particle system with connecting lines that enhances
 * the glassmorphism aesthetic without being distracting.
 */
const ParticleBackground = () => {
  const [init, setInit] = React.useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const options = useMemo(
    () => ({
      background: {
        color: {
          value: 'transparent',
        },
      },
      fpsLimit: 60,
      interactivity: {
        events: {
          onClick: {
            enable: true,
            mode: 'push',
          },
          onHover: {
            enable: true,
            mode: 'bubble',
          },
        },
        modes: {
          push: {
            quantity: 4,
          },
          bubble: {
            distance: 140,
            size: 8,
            duration: 2,
            opacity: 0.5,
          },
        },
      },
      particles: {
        color: {
          value: ['#a5b4fc', '#f472b6', '#22d3ee', '#e0f2fe'],
        },
        links: {
          color: '#818cf8',
          distance: 140,
          enable: true,
          opacity: 0.25,
          width: 1,
        },
        move: {
          direction: 'none',
          enable: true,
          outModes: {
            default: 'bounce',
          },
          random: true,
          speed: 0.8,
          straight: false,
        },
        number: {
          density: {
            enable: true,
          },
          value: 140,
        },
        opacity: {
          value: {
            min: 0.1,
            max: 0.4,
          },
          animation: {
            enable: true,
            speed: 1,
            sync: false,
          },
        },
        shape: {
          type: 'circle',
        },
        size: {
          value: {
            min: 1,
            max: 3,
          },
        },
      },
      detectRetina: true,
    }),
    []
  );

  if (!init) {
    return null;
  }

  return (
    <Particles
      id="tsparticles"
      options={options}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    />
  );
};

export default ParticleBackground;
